/**
 * Per `character-workflow-engine` capability. Defines the per-character
 * workflow document — a JSON DAG of nodes + edges — plus the run-history
 * row schema. Both are also the wire format for the admin REST API.
 *
 * The MODULE_REGISTRY in `workflow-modules.ts` is the single source of
 * truth for module types + their per-node config schemas; the validators
 * in this file dispatch into that registry. Editor (admin-ui) and
 * executor (Lambda) BOTH import from the registry so adding a future
 * module type is one PR in one file.
 */
import { z } from 'zod';
// ── Node / edge / workflow schemas ────────────────────────────────
export const WorkflowNodeSchema = z.object({
    /** Unique within the workflow. */
    id: z.string().uuid(),
    /** Module id (key in MODULE_REGISTRY). Validated by validateWorkflow. */
    type: z.string().min(1).max(80),
    /** React Flow canvas position. */
    position: z.object({
        x: z.number().finite(),
        y: z.number().finite(),
    }),
    /** Module-specific config — validated against MODULE_REGISTRY[type].configSchema. */
    config: z.unknown(),
});
export const WorkflowEdgeSchema = z.object({
    id: z.string().uuid(),
    /** Source node id. */
    source: z.string().uuid(),
    /** Target node id. */
    target: z.string().uuid(),
    /** Port id on the source node (e.g. 'out', 'true', 'false'). */
    sourcePort: z.string().min(1),
    /** Port id on the target node (e.g. 'in'). */
    targetPort: z.string().min(1),
});
export const WorkflowSchema = z.object({
    /** Document uuid. */
    id: z.string().uuid(),
    /** FK to characters table. */
    characterId: z.string().uuid(),
    /** Operator-supplied label, ≤ 80 chars. */
    name: z.string().min(1).max(80),
    /** Enabled workflows are wired to their EventBridge Scheduler (for
     *  trigger.schedule) AND callable via /run. Disabled = stored only. */
    enabled: z.boolean(),
    /** Empty array allowed (draft state). Execution-time check is
     *  separate (executor refuses if no trigger). */
    nodes: z.array(WorkflowNodeSchema),
    edges: z.array(WorkflowEdgeSchema),
    /** Set by the REST handler when an EventBridge Scheduler entry is
     *  wired. Used at delete/disable to remove the entry. */
    schedulerName: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ── Run history ────────────────────────────────────────────────────
export const WorkflowRunNodeStateSchema = z.object({
    status: z.enum(['pending', 'running', 'completed', 'errored', 'skipped']),
    startedAt: z.string().datetime().optional(),
    finishedAt: z.string().datetime().optional(),
    /** Truncated at 4 KB by the executor. JSON-stringifiable. */
    output: z.unknown().optional(),
    /** Human-readable error reason shown in the admin run-history drawer. */
    error: z.string().optional(),
});
export const WorkflowRunSchema = z.object({
    workflowId: z.string().uuid(),
    /** ISO datetime; also the SK in DDB. */
    runStartedAt: z.string().datetime(),
    characterId: z.string().uuid(),
    triggerSource: z.enum(['schedule', 'fan-event', 'manual']),
    /** Free-form payload from the trigger (fanId on fan-event, etc.). */
    triggerPayload: z.unknown().optional(),
    /** Map of nodeId → per-node status snapshot. */
    nodeStates: z.record(z.string().uuid(), WorkflowRunNodeStateSchema),
    finalStatus: z.enum(['succeeded', 'partial', 'failed', 'invalid', 'rolled-back']),
    durationMs: z.number().int().min(0),
    /** TTL — auto-expire 30 days after the run. */
    expiresAt: z.number().int().optional(),
});
// ── Trigger / event taxonomies ─────────────────────────────────────
/** Fan-event types v1 schema knows about. Phase 2 will publish them;
 *  Phase 1 only accepts them via manual test invokes. */
export const FAN_EVENT_TYPES = [
    'chat-started',
    'acquired-character',
    'tier-up',
    'tier-down',
    'inactive-days',
];
/** Pure DAG-shape validation. Empty arrays are tolerated (a draft
 *  workflow with no nodes returns []). Save-time vs. execute-time
 *  triggers are differentiated by the `requireExecutable` flag. */
export function validateDag(workflow, 
/** When true, also requires a trigger node to be present. Execute-time
 *  path uses this; save-time path doesn't (drafts may have no trigger). */
requireExecutable, 
/** Optional set of valid trigger module ids. When omitted, any node
 *  type starting with 'trigger.' is treated as a trigger. */
triggerTypes) {
    const errors = [];
    const nodeIds = new Set();
    const dupIds = new Set();
    for (const n of workflow.nodes) {
        if (nodeIds.has(n.id)) {
            dupIds.add(n.id);
            errors.push({ code: 'NODE_DUPLICATE_ID', message: `node id duplicated: ${n.id}`, ref: n.id });
        }
        nodeIds.add(n.id);
    }
    const isTriggerType = (t) => triggerTypes ? triggerTypes.has(t) : t.startsWith('trigger.');
    const triggers = workflow.nodes.filter((n) => isTriggerType(n.type));
    if (requireExecutable) {
        if (triggers.length === 0) {
            errors.push({ code: 'TRIGGER_MISSING', message: 'workflow has no trigger node', ref: '' });
        }
        else if (triggers.length > 1) {
            errors.push({ code: 'TRIGGER_MULTIPLE', message: `workflow has ${triggers.length} trigger nodes; exactly one is required`, ref: triggers[1].id });
        }
    }
    for (const e of workflow.edges) {
        if (!nodeIds.has(e.source) || dupIds.has(e.source)) {
            errors.push({ code: 'EDGE_DANGLING_SOURCE', message: `edge ${e.id} source ${e.source} not found`, ref: e.id });
        }
        if (!nodeIds.has(e.target) || dupIds.has(e.target)) {
            errors.push({ code: 'EDGE_DANGLING_TARGET', message: `edge ${e.id} target ${e.target} not found`, ref: e.id });
        }
    }
    // Cycle detection — depth-first. Only run when no other structural errors,
    // to avoid false positives from dangling edges.
    if (errors.length === 0) {
        const cycle = detectCycle(workflow);
        if (cycle) {
            errors.push({ code: 'CYCLE', message: `cycle detected through nodes: ${cycle.join(' → ')}`, ref: cycle[0] ?? '' });
        }
    }
    return errors;
}
function detectCycle(workflow) {
    const adj = new Map();
    for (const n of workflow.nodes)
        adj.set(n.id, []);
    for (const e of workflow.edges)
        adj.get(e.source)?.push(e.target);
    const state = new Map();
    for (const n of workflow.nodes)
        state.set(n.id, 'unvisited');
    const stack = [];
    function dfs(id) {
        state.set(id, 'visiting');
        stack.push(id);
        for (const next of adj.get(id) ?? []) {
            const s = state.get(next);
            if (s === 'visiting') {
                const cycleStart = stack.indexOf(next);
                return stack.slice(cycleStart).concat(next);
            }
            if (s === 'unvisited') {
                const found = dfs(next);
                if (found)
                    return found;
            }
        }
        state.set(id, 'done');
        stack.pop();
        return null;
    }
    for (const n of workflow.nodes) {
        if (state.get(n.id) === 'unvisited') {
            const found = dfs(n.id);
            if (found)
                return found;
        }
    }
    return null;
}
// ── Topological sort ───────────────────────────────────────────────
//
// Kahn's algorithm. Used by the executor to walk nodes in dependency
// order. Returns null if there's a cycle (executor should reject upstream).
export function topoSort(workflow) {
    const indeg = new Map();
    const adj = new Map();
    for (const n of workflow.nodes) {
        indeg.set(n.id, 0);
        adj.set(n.id, []);
    }
    for (const e of workflow.edges) {
        indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1);
        adj.get(e.source)?.push(e.target);
    }
    const queue = [];
    for (const [id, d] of indeg)
        if (d === 0)
            queue.push(id);
    const order = [];
    while (queue.length > 0) {
        const id = queue.shift();
        order.push(id);
        for (const next of adj.get(id) ?? []) {
            const nextDeg = (indeg.get(next) ?? 0) - 1;
            indeg.set(next, nextDeg);
            if (nextDeg === 0)
                queue.push(next);
        }
    }
    if (order.length !== workflow.nodes.length)
        return null;
    return order;
}
